import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GstReturnTracker = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedFY, setSelectedFY] = useState(getCurrentFinancialYear());
  const [returnData, setReturnData] = useState([]);
  const [apiUsage, setApiUsage] = useState({ calls: 0, credits: 0, lastSync: null });
  const [isLoading, setIsLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [syncProgress, setSyncProgress] = useState({ show: false, current: 0, total: 0, message: '' });

  useEffect(() => {
    fetchClients();
    fetchApiUsage();
    
    // Auto-refresh every 24 hours
    const interval = setInterval(() => {
      if (autoRefresh && selectedClient) {
        fetchGstReturns(selectedClient, selectedFY, true);
      }
    }, 24 * 60 * 60 * 1000); // 24 hours in milliseconds

    return () => clearInterval(interval);
  }, [autoRefresh, selectedClient, selectedFY]);

  // Fetch GST returns when client or FY changes
  useEffect(() => {
    if (selectedClient && selectedFY) {
      fetchGstReturns(selectedClient, selectedFY, true);
    }
  }, [selectedClient, selectedFY]);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      const gstClients = response.data.filter(client => client.gst_enabled && client.gstin);
      setClients(gstClients);
      if (gstClients.length > 0 && !selectedClient) {
        setSelectedClient(gstClients[0].id);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchApiUsage = async () => {
    try {
      const response = await axios.get(`${API}/gst-returns/usage`);
      setApiUsage(response.data);
    } catch (error) {
      console.error('Error fetching API usage:', error);
    }
  };

  const fetchGstReturns = async (clientId, fy, isBackground = false) => {
    if (!isBackground) setIsLoading(true);
    
    try {
      const response = await axios.get(`${API}/gst-returns/${clientId}/${fy}`);
      const periods = [...new Set(response.data.map(r => r.return_period))].sort();
      
      console.log('📊 GST Returns API Response:', {
        selectedFY: fy,
        dataCount: response.data.length,
        periodsInData: periods,
        sampleData: response.data.slice(0, 2),
        message: response.data.length === 0 ? '⚠️ No data for this FY. Try syncing this FY first.' : '✅ Data loaded'
      });
      
      setReturnData(response.data);
      setLastRefresh(new Date());
      
      // Show alert if no data for selected FY
      if (!isBackground && response.data.length === 0) {
        alert(`⚠️ No GST return data found for FY ${fy}\n\nThis could mean:\n• Data not synced yet for this financial year\n• Click "Sync All Clients" to fetch data for ${fy}\n• Or select a different financial year from dropdown`);
      }
      
      if (!isBackground) {
        await fetchApiUsage(); // Update usage stats
      }
    } catch (error) {
      console.error('Error fetching GST returns:', error);
      alert('Error fetching GST return data');
    } finally {
      if (!isBackground) setIsLoading(false);
    }
  };

  const handleSyncAll = async () => {
    setIsLoading(true);
    
    // Get GST-enabled clients
    const gstClients = clients.filter(c => c.gst_enabled && c.gstin);
    
    if (gstClients.length === 0) {
      alert('No GST-enabled clients found. Please add clients with GSTIN first.');
      setIsLoading(false);
      return;
    }
    
    // Show progress bar
    setSyncProgress({ show: true, current: 0, total: gstClients.length, message: 'Starting sync...' });
    
    try {
      // Sync each client individually to show progress
      let synced = 0;
      let failed = 0;
      
      for (let i = 0; i < gstClients.length; i++) {
        const client = gstClients[i];
        setSyncProgress({ 
          show: true, 
          current: i + 1, 
          total: gstClients.length, 
          message: `Syncing ${client.name}...` 
        });
        
        try {
          // Fetch GST returns for this client (will trigger API call if cache is stale)
          await axios.get(`${API}/gst-returns/${client.id}/${selectedFY}`);
          synced++;
          
          // Small delay to show progress
          await new Promise(resolve => setTimeout(resolve, 500));
        } catch (error) {
          console.error(`Error syncing ${client.name}:`, error);
          failed++;
        }
      }
      
      setSyncProgress({ show: true, current: gstClients.length, total: gstClients.length, message: 'Sync complete!' });
      
      // Refresh current client data
      if (selectedClient) {
        await fetchGstReturns(selectedClient, selectedFY);
      }
      await fetchApiUsage();
      
      // Show result
      setTimeout(() => {
        setSyncProgress({ show: false, current: 0, total: 0, message: '' });
        alert(`Sync completed!\n✅ Successfully synced: ${synced} clients\n${failed > 0 ? `❌ Failed: ${failed} clients` : ''}`);
      }, 1000);
      
    } catch (error) {
      console.error('Error syncing all returns:', error);
      setSyncProgress({ show: false, current: 0, total: 0, message: '' });
      alert('Error syncing GST returns. Please check your internet connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const colorClass = GST_STATUS_COLORS[status] || 'bg-gray-400 text-white';
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colorClass}`}>
        {status}
      </span>
    );
  };

  const getReturnTypeIcon = (returnType) => {
    const icons = {
      'GSTR1': '📊',
      'GSTR3B': '💰', 
      'CMP08': '🏢',
      'GSTR4': '📋'
    };
    return icons[returnType] || '📄';
  };

  const generateMonthsGrid = () => {
    const months = [
      { name: 'Apr', month: 4 }, { name: 'May', month: 5 }, { name: 'Jun', month: 6 },
      { name: 'Jul', month: 7 }, { name: 'Aug', month: 8 }, { name: 'Sep', month: 9 },
      { name: 'Oct', month: 10 }, { name: 'Nov', month: 11 }, { name: 'Dec', month: 12 },
      { name: 'Jan', month: 1 }, { name: 'Feb', month: 2 }, { name: 'Mar', month: 3 }
    ];
    
    const [startYear, endYear] = selectedFY.split('-').map(y => y.length === 2 ? '20' + y : y);
    console.log('📅 FY Calculation:', { selectedFY, startYear, endYear });
    
    return months.map((monthInfo, index) => {
      // Determine which year this month belongs to
      const year = monthInfo.month >= 4 ? startYear : endYear;
      const monthStr = String(monthInfo.month).padStart(2, '0');
      const periodPrefix = `${monthStr}${year}`;
      
      // Filter returns for this specific month and year
      const monthData = returnData.filter(item => 
        item.return_period && item.return_period === periodPrefix
      );
      
      if (index === 0) { // Debug first month (April)
        console.log('🔍 April Debug:', {
          selectedFY,
          yearCalculation: { startYear, endYear },
          periodPrefix,
          lookingFor: `Should find returns with period: ${periodPrefix}`,
          monthDataCount: monthData.length,
          totalReturnData: returnData.length,
          allPeriodsInData: [...new Set(returnData.map(r => r.return_period))].sort(),
          sampleReturn: returnData[0],
          explanation: `Frontend is looking for period "${periodPrefix}" but data might have different periods`
        });
      }

      return (
        <div key={`${monthInfo.name}-${year}`} className="bg-gradient-to-br from-white to-gray-50 rounded-lg shadow-md border border-gray-200 p-4 hover:shadow-lg transition-shadow">
          <h4 className="font-bold text-gray-800 mb-3 text-center border-b pb-2">
            {monthInfo.name} {year}
          </h4>
          <div className="space-y-2">
            {Object.keys(GST_RETURN_TYPES).map(returnType => {
              const returnItem = monthData.find(item => item.return_type === returnType);
              const status = returnItem ? returnItem.status : 'Not Due';
              
              return (
                <div key={returnType} className="flex items-center justify-between p-2 bg-white rounded hover:bg-gray-50 transition-colors">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm">{getReturnTypeIcon(returnType)}</span>
                    <span className="text-xs font-medium text-gray-700">{GST_RETURN_TYPES[returnType].name}</span>
                  </div>
                  <div className="flex flex-col items-end">
                    {getStatusBadge(status)}
                    {returnItem && returnItem.filing_date && (
                      <span className="text-xs text-gray-500 mt-1">
                        {new Date(returnItem.filing_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    });
  };

  const selectedClientData = clients.find(c => c.id === selectedClient);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-red-50">
      {/* Header Section */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-orange-100/50 px-6 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent mb-2">
                GST Return Tracker
              </h1>
              <p className="text-gray-600 text-lg">Real-time GST filing status with RETTRACK API integration</p>
            </div>
            <div className="mt-4 md:mt-0 flex items-center space-x-4">
              <div className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-3 rounded-2xl shadow-lg text-center">
                <div className="text-sm font-medium">API Calls Today</div>
                <div className="text-2xl font-bold">{apiUsage.calls}</div>
              </div>
              <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-3 rounded-2xl shadow-lg text-center">
                <div className="text-sm font-medium">Credits Used</div>
                <div className="text-2xl font-bold">{apiUsage.credits}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 max-w-7xl mx-auto space-y-8">
        {/* Control Panel */}
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-2xl">
          <CardHeader className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
            <CardTitle className="text-2xl font-bold flex items-center">
              <Settings className="w-6 h-6 mr-3" />
              GST Return Control Panel
            </CardTitle>
            <CardDescription className="text-indigo-100">
              Select client and financial year to track GST return compliance
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-6">
              {/* Filters Row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <Label htmlFor="client">Select Client</Label>
                  <Select value={selectedClient} onValueChange={setSelectedClient}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.name} - {client.gstin}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="fy">Financial Year</Label>
                  <Select value={selectedFY} onValueChange={setSelectedFY}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2025-26">2025-26</SelectItem>
                      <SelectItem value="2024-25">2024-25 {getCurrentFinancialYear() === '2024-25' ? '(Current)' : ''}</SelectItem>
                      <SelectItem value="2023-24">2023-24 {getCurrentFinancialYear() === '2023-24' ? '(Current)' : ''}</SelectItem>
                      <SelectItem value="2022-23">2022-23</SelectItem>
                      <SelectItem value="2021-22">2021-22</SelectItem>
                      <SelectItem value="2020-21">2020-21</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-end">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="autoRefresh"
                      checked={autoRefresh}
                      onChange={(e) => setAutoRefresh(e.target.checked)}
                      className="rounded"
                    />
                    <Label htmlFor="autoRefresh" className="text-sm">Auto-refresh (24h)</Label>
                  </div>
                </div>
              </div>

              {/* Actions Row */}
              <div className="flex flex-wrap gap-3">
                <Button 
                  onClick={() => selectedClient && fetchGstReturns(selectedClient, selectedFY)}
                  disabled={isLoading || !selectedClient}
                  className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 shadow-lg hover:shadow-xl transition-all duration-200"
                >
                  {isLoading ? 'Syncing...' : 'Sync Client'}
                </Button>
                <Button 
                  onClick={handleSyncAll}
                  disabled={isLoading}
                  className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 shadow-lg hover:shadow-xl transition-all duration-200"
                >
                  Sync All
                </Button>
                <Button 
                  onClick={() => {
                    // Calculate previous month
                    const now = new Date();
                    const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                    navigate('/gst-at-glance', { 
                      state: { 
                        financialYear: selectedFY, 
                        month: prevMonth.getMonth() + 1 
                      } 
                    });
                  }}
                  className="bg-gradient-to-r from-purple-500 to-indigo-600"
                >
                  📊 At Glance
                </Button>
              </div>
            </div>

            {/* Progress Bar for Sync */}
            {syncProgress.show && (
              <div className="mt-6 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-blue-800 mb-3 text-center">
                  🔄 Syncing GST Returns
                </h3>
                <div className="mb-3">
                  <div className="flex justify-between text-sm text-blue-700 mb-2">
                    <span>{syncProgress.message}</span>
                    <span className="font-semibold">{syncProgress.current} / {syncProgress.total}</span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-4 overflow-hidden shadow-inner">
                    <div 
                      className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-500 ease-out flex items-center justify-end pr-2"
                      style={{ width: `${(syncProgress.current / syncProgress.total) * 100}%` }}
                    >
                      <span className="text-white text-xs font-bold">
                        {Math.round((syncProgress.current / syncProgress.total) * 100)}%
                      </span>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-blue-600 text-center animate-pulse">
                  Please wait while we fetch GST return data from RETTRACK API...
                </p>
              </div>
            )}

            {selectedClientData && (
              <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
                <h3 className="font-semibold text-purple-800 mb-2">Selected Client Details</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-purple-700">Name:</span>
                    <div className="text-purple-600">{selectedClientData.name}</div>
                  </div>
                  <div>
                    <span className="font-medium text-purple-700">GSTIN:</span>
                    <div className="text-purple-600 font-mono">{selectedClientData.gstin}</div>
                  </div>
                  <div>
                    <span className="font-medium text-purple-700">State:</span>
                    <div className="text-purple-600">{selectedClientData.state}</div>
                  </div>
                  <div>
                    <span className="font-medium text-purple-700">Last Sync:</span>
                    <div className="text-purple-600">
                      {lastRefresh ? lastRefresh.toLocaleTimeString() : 'Never'}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* GST Return Status Matrix */}
        {selectedClient && (
          <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-2xl">
            <CardHeader className="bg-gradient-to-r from-green-500 to-emerald-600 text-white">
              <CardTitle className="text-2xl font-bold flex items-center">
                <BarChart3 className="w-6 h-6 mr-3" />
                GST Return Filing Status - FY {selectedFY}
              </CardTitle>
              <CardDescription className="text-green-100">
                Real-time filing status matrix for {selectedClientData?.name}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              {/* Status Legend */}
              <div className="mb-6 flex flex-wrap gap-4 justify-center">
                {Object.entries(GST_STATUS_COLORS).map(([status, colorClass]) => (
                  <div key={status} className="flex items-center space-x-2">
                    <span className={`w-4 h-4 rounded-full ${colorClass}`}></span>
                    <span className="text-sm font-medium">{status}</span>
                  </div>
                ))}
              </div>

              {/* Monthly Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {generateMonthsGrid()}
              </div>

              {isLoading && (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                  <span className="ml-2 text-gray-600">Fetching latest GST return data...</span>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* API Usage Analytics */}
        <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-2xl">
          <CardHeader className="bg-gradient-to-r from-yellow-500 to-orange-600 text-white">
            <CardTitle className="text-2xl font-bold flex items-center">
              <TrendingUp className="w-6 h-6 mr-3" />
              API Usage & Analytics
            </CardTitle>
            <CardDescription className="text-yellow-100">
              Monitor RETTRACK API consumption and performance metrics
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-3xl font-bold text-blue-600">{apiUsage.calls}</div>
                <div className="text-blue-800 font-medium">Total API Calls</div>
                <div className="text-blue-600 text-sm">Today</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-3xl font-bold text-green-600">{apiUsage.credits}</div>
                <div className="text-green-800 font-medium">Credits Consumed</div>
                <div className="text-green-600 text-sm">This Month</div>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <div className="text-3xl font-bold text-purple-600">{clients.length}</div>
                <div className="text-purple-800 font-medium">GST Clients</div>
                <div className="text-purple-600 text-sm">Active</div>
              </div>
              <div className="text-center p-4 bg-orange-50 rounded-lg">
                <div className="text-3xl font-bold text-orange-600">
                  {apiUsage.lastSync ? new Date(apiUsage.lastSync).toLocaleDateString() : 'Never'}
                </div>
                <div className="text-orange-800 font-medium">Last Sync</div>
                <div className="text-orange-600 text-sm">Date</div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 mr-2" />
                <div className="text-sm">
                  <p className="font-medium text-yellow-800">Smart Caching Enabled</p>
                  <ul className="text-yellow-700 mt-1 space-y-1">
                    <li>• Individual client data cached for 6 hours</li>
                    <li>• Bulk sync operations cached for 24 hours</li>
                    <li>• Auto-refresh respects cache to minimize API costs</li>
                    <li>• Real-time updates available for critical compliance tracking</li>
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GstReturnTracker;
